import { PlusOutlined } from "@ant-design/icons";
import Modal from "antd/lib/modal/Modal";
import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Locale } from "../../utils";
import { getUserInfo } from "../../utils/redux/reducers/User";
import Media from "./Media";
import ImageCropModal from "./ImageCropModal";
import { uploadFile } from "./mediaApi";
import { compressImage } from "./compressUtils";
import { useMutation, useQueryClient } from "react-query";
import {
  handleMutationError,
  showNotificationWithIcon,
} from "../../utils/helpers/helpers";
import "./ImageUpload.scss";
import ImageUploadPreview from "./ImageUploadPreview";
import { Spin } from "antd";
const i18n = Locale.getInstance();
const ImageUpload = ({
  images,
  setSelectedImages,
  multiple = false,
  aspectRatio,
}) => {
  const user = useSelector((state) => getUserInfo(state));
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [selectedFiles, selectFiles] = useState(false);
  const [selectedImages, selectImages] = useState([]);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [uploading, setUploading] = useState(false);
  console.log("🚀 ~ ImageUpload ~ uploading:", uploading);

  useEffect(() => {
    if (images && images.length > 0) {
      selectImages(images);
    } else {
      selectImages([]);
    }
  }, [images]);

  const handleFinishSelection = () => {
    selectImages(selectedFiles);
    setSelectedImages(selectedFiles);

    setOpenUploadModal(false);
  };
  const hideModal = () => {
    setOpenUploadModal(false);
  };

  const showConfirm = () => {
    // If aspect ratio is specified, show file input for direct upload with crop
    // Otherwise, show media modal
    if (aspectRatio) {
      fileInputRef.current?.click();
    } else {
      setOpenUploadModal(true);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageToCrop(event.target.result);
        setCropModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
    // Reset input
    e.target.value = "";
  };

  const uploadMutation = useMutation(uploadFile, {
    onSuccess: () => {
      queryClient.invalidateQueries("media-list");
      setUploading(false);
    },
    onError: (error) => {
      setUploading(false);
      handleMutationError(error);
    },
  });


  const handleCropComplete = async (croppedFile) => {
    if (!croppedFile || !user?.token) {
      showNotificationWithIcon(
        i18n.t("productEdit.uploadError") || "上传失败",
        "error"
      );
      return;
    }

    setUploading(true);
    setCropModalOpen(false);
    setImageToCrop(null);

    try {
      // Compress the image before uploading
      const compressedFile = await compressImage(croppedFile);
      const uploadFiles = [{ file: compressedFile }];
      const res = await uploadMutation.mutateAsync({
        type: "image",
        data: {
          files: uploadFiles,
          folderId: "",
        },
        token: user.token,
        config: {},
      });

      // Handle different response formats
      let uploadedImage = null;

      if (res && res.data) {
        // Check if data is an array (old format)
        if (Array.isArray(res.data) && res.data.length > 0) {
          uploadedImage = res.data[0];
        }
        // Check if data is an object with url property (new format)
        else if (res.data.url) {
          uploadedImage = res.data;
        }
        // Check if data.data exists (nested format)
        else if (
          res.data.data &&
          Array.isArray(res.data.data) &&
          res.data.data.length > 0
        ) {
          uploadedImage = res.data.data[0];
        }
      }

      if (uploadedImage) {
        // Ensure the uploaded image has the url property
        if (!uploadedImage.url) {
          if (uploadedImage.path) {
            uploadedImage.url = uploadedImage.path;
          } else if (uploadedImage.file_path) {
            uploadedImage.url = uploadedImage.file_path;
          } else {
            console.error(
              "Uploaded image missing url property:",
              uploadedImage
            );
            throw new Error("Uploaded image is missing url property");
          }
        }

        const newImages = multiple
          ? [...selectedImages, uploadedImage]
          : [uploadedImage];

        // Update local state first
        selectImages(newImages);
        // Then update parent state
        setSelectedImages(newImages);

        showNotificationWithIcon(
          i18n.t("media.saveFinished") || "上传成功",
          "success"
        );
      } else {
        console.error("Invalid upload response:", res);
        throw new Error("Upload response is invalid");
      }
    } catch (error) {
      console.error("Upload error:", error);
      showNotificationWithIcon(
        error.message || i18n.t("productEdit.uploadError") || "上传失败",
        "error"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setImageToCrop(null);
  };

  if (!multiple && selectedImages.length > 0) {
    return (
      <div className="image-upload">
        <div className="upload-container">
          <ImageUploadPreview
            uploading={uploading}
            selectedImages={selectedImages}
            selectImages={selectImages}
            aspectRatio={aspectRatio}
          />
        </div>
        {aspectRatio && (
          <div className="aspect-ratio-tip">
            {i18n
              .t("productEdit.aspectRatioTip", { ratio: aspectRatio })
              .replace("{ratio}", aspectRatio)}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="image-upload">
        <div className="upload-container">
          <ImageUploadPreview
            uploading={uploading}
            selectedImages={selectedImages}
            selectImages={selectImages}
            aspectRatio={aspectRatio}
          />
          <div
            className={`uploading-overlay ${
              aspectRatio ? `aspect-ratio-${aspectRatio.replace(":", "-")}` : ""
            }`}
            style={uploading ? { display: "flex" } : { display: "none" }}
          >
            <Spin
              spinning={uploading}
              className="uploading-spin"
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1001,
              }}
            />
          </div>
          <div
            className={`upload-button ${
              aspectRatio ? `aspect-ratio-${aspectRatio.replace(":", "-")}` : ""
            }`}
            onClick={showConfirm}
          >
            <PlusOutlined />
            <div className="ant-upload-text">
              {i18n.t("productEdit.imagesUpload")}
            </div>
          </div>
        </div>
        {aspectRatio && (
          <>
            <div className="aspect-ratio-tip">
              {i18n
                .t("productEdit.aspectRatioTip", { ratio: aspectRatio })
                .replace("{ratio}", aspectRatio)}
            </div>
            <div
              className="media-library-link"
              onClick={() => setOpenUploadModal(true)}
              style={{
                marginTop: 4,
                fontSize: 12,
                color: "#1890ff",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {i18n.t("productEdit.selectFromLibrary") || "或从媒体库选择"}
            </div>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />
      </div>

      <ImageCropModal
        open={cropModalOpen}
        imageSrc={imageToCrop}
        aspectRatio={aspectRatio}
        onCancel={handleCropCancel}
        onComplete={handleCropComplete}
      />

      <Modal
        title={i18n.t("media.selectImages")}
        open={openUploadModal}
        onOk={handleFinishSelection}
        onCancel={hideModal}
        okText={i18n.t("base.modal.okText")}
        cancelText={i18n.t("base.modal.cancelText")}
        width={880}
      >
        <Media selectFiles={selectFiles} collectionName="images" />
      </Modal>
    </>
  );
};

export default ImageUpload;
